import { Button, Dialog } from "@justworkshr/milo-components";
import { Form, Formik } from "formik";
import { editDependentInitialFormValues } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { employeeDependentValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import { useState } from "react";
import { SingleDependentType } from "types";
import styles from "./CurrentEmployees.module.css";
import isEqual from "lodash/isEqual";
import DependentFormComponent from "./DependentForm";
import { EditDependentFormType } from "pages/CensusAndCurrentInformation/utils/formTypes";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  useUpdateDependentCensusesMutation,
} from "types/generated/operations";
import { ConvertToISODate, errorMessageMap } from "utils/helper";
import { getProspectUuid } from "utils/idHelpers";
import { trackFormError } from "utils/analytics/trackEvent";
import Alert from "uicomponents/Alert";
interface IEditUpsellDependentProps {
  data: SingleDependentType;
  closeParentDialog: () => void;
  updateDependentList: (
    newDependent: SingleDependentType,
    method: "Delete" | "Add" | "Edit"
  ) => void;
}

const EditUpsellDependent = (props: IEditUpsellDependentProps) => {
  const { closeParentDialog, data, updateDependentList } = props;
  const initialFormValues = editDependentInitialFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [isEditDependentDialogOpen, setIsEditDependentDialogOpen] =
    useState(true);

  const [updateDependentCensuses, { loading: updateDependentCensusesLoading }] =
    useUpdateDependentCensusesMutation();

  const closeDialog = (newValues: any) => {
    if (isEqual(initialFormValues, newValues)) {
      setIsEditDependentDialogOpen(false);
      closeParentDialog();
    } else {
      setIsEditDependentDialogOpen(false);
      setIsExitDialogOpen(true);
    }
  };

  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const closeExitDialog = () => {
    setIsExitDialogOpen(false);
    closeParentDialog();
  };

  const swapModalAndGoBackToEditingDependents = () => {
    setIsExitDialogOpen(false);
    setIsEditDependentDialogOpen(true);
  };

  const handleOnSubmit = async (values: EditDependentFormType) => {
    try {
      await updateDependentCensuses({
        variables: {
          prospectId: getProspectUuid(),
          dependentCensuses: [
            {
              employeeId: data.employeeCensusId,
              gender: values.gender
                .toUpperCase()
                .replace(/-/g, "_") as GenderTypeEnum,
              firstName: values.firstName,
              lastName: values.lastName,
              zip: values.homeZip,
              participantType: values.participantType
                .toUpperCase()
                .replace(/-/g, "_") as ParticipantTypeEnum,
              dateOfBirth: ConvertToISODate(values.dateOfBirth),
              uuid: data.uuid,
            },
          ],
        },
      });

      const newDependent = {
        uuid: data.uuid || "",
        employeeCensusId: data.employeeCensusId,
        gender: values.gender
          .toUpperCase()
          .replace(/-/g, "_") as GenderTypeEnum,
        firstName: values.firstName,
        lastName: values.lastName,
        homeZip: values.homeZip,
        participantType: values.participantType,
        dateOfBirth: ConvertToISODate(values.dateOfBirth),
      };

      updateDependentList(newDependent, "Edit");
      setIsEditDependentDialogOpen(false);
      closeParentDialog();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };
  return (
    <>
      <div className={styles.addDependentsDialog}>
        <Formik
          initialValues={editDependentInitialFormValues(data)}
          validationSchema={employeeDependentValidationSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values }) => (
            <Dialog
              isOpen={isEditDependentDialogOpen}
              onClose={() => closeDialog(values)}
              title="Edit dependent"
              size="xl"
            >
              <Form>
                <DependentFormComponent />
                {isError && <Alert message={errorMessage} />}
                <div className={styles.footerButton}>
                  <Button variant="ghost" onClick={() => closeDialog(values)}>
                    Cancel
                  </Button>
                  <Button variant="filled" type="submit">
                    {updateDependentCensusesLoading ? "Loading" : "Save"}
                  </Button>
                </div>
              </Form>
            </Dialog>
          )}
        </Formik>
      </div>
      <Dialog
        isOpen={isExitDialogOpen}
        onClose={swapModalAndGoBackToEditingDependents}
        data-testid="exit-dialog"
        title="Exit without saving changes?"
        primaryButton={
          <Button onClick={closeExitDialog} variant="filled">
            Exit
          </Button>
        }
        secondaryButton={
          <Button
            onClick={swapModalAndGoBackToEditingDependents}
            variant="ghost"
          >
            Back
          </Button>
        }
      >
        <p>
          If you leave now, you’ll lose any of the info you’ve already added.
        </p>
        <p>
          Don’t forget, we need info for all dependents for each employee if
          those dependents are enrolled in your current plan or intend to enroll
          in a new plan.
        </p>
      </Dialog>
    </>
  );
};

export default EditUpsellDependent;
